
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ResponsiveOverlay from '@/components/ResponsiveOverlay';
import Button from "../Buttons/Button/Button";
import Title from "../Title/Title";
import "./ShareModal.css";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { toast } from "react-toastify";
import { error, debug } from '@/utils/logger';
import { generateShareLink, adminGenerateShareLink, buildCanonicalShareUrl, type ShareResourceType } from '@/api/share';
import { copyToClipboard } from '@/utils/shareUtils';
import { WebAnalytics } from '@/lib/analytics';
import { useAuth, usePlatformBrowseMode } from '@/contexts';
import { getViewerAnalyticsProps } from '@/lib/contentPageAnalytics';
import ShareButtonIcon from '@/components/ShareButtonIcon/ShareButtonIcon';
import {
  EVENT_SHARE_CLICKED,
  EVENT_SHARED_PUBLICLY,
  EVENT_LOCATION_SHARE_LINK_CREATED,
  EVENT_SHARE_MODAL_DISMISSED,
  EVENT_SHARE_MODAL_OPENED,
  type ShareContentType,
  type ShareLinkType,
  type ShareModalDismissMethod,
  type ShareModalEntry,
} from '@/lib/analyticsEvents';

export interface ShareModalPostPublishMessage {
  title: string;
  subtitle: string;
}

interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Optional: Pre-generated URL for backward compatibility */
  url?: string;
  title?: string;
  resourceType?: ShareResourceType;
  resourceId?: string;
  /** When sharing a post, pass content type for analytics (list, itinerary, place) */
  content_type?: ShareContentType;
  /**
   * When sharing a `profile` or `location` resource, the owner's `profile_type`
   * — creators and advisors emit `/storefront/<id>` as the canonical URL
   * instead of `/profile/<id>`. Travelers (or when unset) stay on `/profile/`.
   */
  ownerProfileType?: string | null;
  /**
   * When set, use POST /api/v2/admin/share/generate with this as created_by_user_id
   * so the resulting link is attributed to the resource owner, not the admin session.
   */
  createdByUserId?: string;
  /** Shown above actions after publishing (e.g. from Post); omit for share-icon entry */
  postPublishMessage?: ShareModalPostPublishMessage | null;
  /** How the modal was opened (for analytics) */
  entry?: ShareModalEntry;
  /**
   * Called after the share URL was copied to the clipboard from the Copy Link button
   * or the URL row (before `onClose`). Use for resource-specific completion events
   * (e.g. enriched recommendation share) — do not rely on `onClose` alone (dismiss without copy).
   */
  onCopyLinkSuccess?: () => void;
}

const ShareModal: React.FC<ShareModalProps> = ({
  isOpen,
  onClose,
  url: providedUrl,
  title = "Share Itinerary",
  resourceType,
  resourceId,
  content_type,
  ownerProfileType,
  createdByUserId,
  postPublishMessage,
  entry = 'other',
  onCopyLinkSuccess,
}) => {
  const { user } = useAuth();
  const { isOpenBrowse, browseMode } = usePlatformBrowseMode();
  const viewerProps = useMemo(
    () => getViewerAnalyticsProps({ hasUser: !!user, browseMode }),
    [user, browseMode],
  );
  const shareLinkType: ShareLinkType =
    !user && isOpenBrowse ? 'canonical' : 'tokenized';
  const [shareUrl, setShareUrl] = useState<string | null>(providedUrl || null);
  const [canonicalUrl, setCanonicalUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const prevIsOpenRef = useRef(false);

  const dismissAndClose = useCallback(
    (method: ShareModalDismissMethod) => {
      WebAnalytics.track(EVENT_SHARE_MODAL_DISMISSED, {
        dismiss_method: method,
        entry,
        ...(resourceType && { resourceType }),
        ...(resourceId && { resourceId }),
        ...(content_type && { content_type }),
      });
      onClose();
    },
    [onClose, entry, resourceType, resourceId, content_type]
  );

  useEffect(() => {
    const wasOpen = prevIsOpenRef.current;
    prevIsOpenRef.current = isOpen;
    if (!isOpen || wasOpen) return;
    WebAnalytics.track(EVENT_SHARE_MODAL_OPENED, {
      entry,
      has_post_publish_message: Boolean(postPublishMessage),
      share_link_type: shareLinkType,
      ...viewerProps,
      ...(resourceType && { resourceType }),
      ...(resourceId && { resourceId }),
      ...(content_type && { content_type }),
    });
  }, [isOpen, entry, postPublishMessage, resourceType, resourceId, content_type, shareLinkType, viewerProps]);

  // Generate share link when modal opens if resourceType/resourceId are provided
  useEffect(() => {
    if (isOpen && resourceType && resourceId && !providedUrl) {
      const generateLink = async () => {
        setLoading(true);
        setErrorMessage(null);
        try {
          if (!user && isOpenBrowse && resourceType && resourceId) {
            const canonical = buildCanonicalShareUrl(resourceType, resourceId, {
              contentType: content_type,
              ownerProfileType,
            });
            setShareUrl(canonical);
            setCanonicalUrl(canonical);
            try {
              WebAnalytics.trackContentInteraction(EVENT_SHARED_PUBLICLY, {
                resourceType,
                resourceId,
                ...(content_type && { content_type }),
                share_link_type: 'canonical' satisfies ShareLinkType,
                ...viewerProps,
                entry,
              });
            } catch (analyticsErr) {
              error('[ShareModal] Analytics failed (canonical share still valid)', analyticsErr);
            }
            return;
          }

          debug('[ShareModal] Generating share link:', { resourceType, resourceId, createdByUserId });
          const response = createdByUserId
            ? await adminGenerateShareLink({
                resource_type: resourceType,
                resource_id: resourceId,
                created_by_user_id: createdByUserId,
              }, { ownerProfileType })
            : await generateShareLink({
                resource_type: resourceType,
                resource_id: resourceId,
                ...(user?.cognito_username?.trim() && {
                  creator_ref: user.cognito_username.trim(),
                }),
              }, { contentType: content_type, ownerProfileType });
          setShareUrl(response.share_url);
          debug('[ShareModal] Share link ready', {
            resourceType,
            resourceId,
            share_url: response.share_url,
            has_short_url: !!response.short_url,
          });

          try {
            if (resourceType === 'location') {
              WebAnalytics.trackContentInteraction(EVENT_LOCATION_SHARE_LINK_CREATED, {
                location_id: resourceId,
                share_token: response.share_token,
                ...(response.created_by && { creator_id: response.created_by }),
              });
            } else {
              WebAnalytics.trackContentInteraction(EVENT_SHARED_PUBLICLY, {
                resourceType,
                resourceId,
                ...(content_type && { content_type }),
                share_token: response.share_token,
                share_link_type: 'tokenized' satisfies ShareLinkType,
                ...viewerProps,
                ...(response.created_by && { creator_id: response.created_by }),
              });
            }
          } catch (analyticsErr) {
            error('[ShareModal] Analytics failed (share link still valid)', analyticsErr);
          }
        } catch (shareError) {
          error('[ShareModal] Error generating share link:', shareError);
          if (!user && isOpenBrowse && resourceType && resourceId) {
            const canonical = buildCanonicalShareUrl(resourceType, resourceId, {
              contentType: content_type,
              ownerProfileType,
            });
            setShareUrl(canonical);
            setCanonicalUrl(canonical);
            try {
              WebAnalytics.trackContentInteraction(EVENT_SHARED_PUBLICLY, {
                resourceType,
                resourceId,
                ...(content_type && { content_type }),
                share_link_type: 'canonical' satisfies ShareLinkType,
                ...viewerProps,
                entry,
              });
            } catch (analyticsErr) {
              error('[ShareModal] Analytics failed (canonical fallback still valid)', analyticsErr);
            }
            return;
          }
          setErrorMessage('Failed to generate share link. Please try again.');
          toast.error('Failed to generate share link');
        } finally {
          setLoading(false);
        }
      };
      generateLink();
    } else if (isOpen && providedUrl) {
      // Use provided URL if available
      setShareUrl(providedUrl);
      if (resourceType && resourceId) {
        setCanonicalUrl(buildCanonicalShareUrl(resourceType, resourceId, {
          contentType: content_type,
          ownerProfileType,
        }));
      }
    }
  }, [isOpen, resourceType, resourceId, providedUrl, content_type, ownerProfileType, createdByUserId, user, isOpenBrowse, viewerProps, entry]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setShareUrl(providedUrl || null);
      setErrorMessage(null);
      setLoading(false);
    }
  }, [isOpen, providedUrl]);

  const copyUrl = useCallback(
    async (url: string | null, copyVia: 'copy_button' | 'url_field', onSuccess?: () => void) => {
      if (!url) {
        if (copyVia === 'copy_button') {
          toast.error('Share link not available');
        }
        return;
      }
      try {
        WebAnalytics.trackContentInteraction(EVENT_SHARE_CLICKED, {
          resourceType,
          resourceId,
          ...(content_type && { content_type }),
          share_method: 'copy_link',
          share_link_type: shareLinkType,
          ...viewerProps,
          entry,
          copy_via: copyVia,
        });

        await copyToClipboard(url);
        onSuccess?.();
      } catch (copyError) {
        error('Failed to copy link:', copyError);
        if (copyVia === 'copy_button') {
          toast.error('Failed to copy link. Please try again.');
        }
      }
    },
    [resourceType, resourceId, content_type, entry, shareLinkType, viewerProps]
  );

  const copyShareLinkAndFinish = useCallback(
    async (copyVia: 'copy_button' | 'url_field') => {
      await copyUrl(shareUrl, copyVia, () => {
        try {
          onCopyLinkSuccess?.();
        } catch (cbErr) {
          error('[ShareModal] onCopyLinkSuccess failed', cbErr);
        }
        onClose();
      });
    },
    [shareUrl, onClose, onCopyLinkSuccess, copyUrl]
  );

  const handleCopyLink = useCallback(() => {
    void copyShareLinkAndFinish('copy_button');
  }, [copyShareLinkAndFinish]);

  const handleUrlClick = useCallback(() => {
    void copyShareLinkAndFinish('url_field');
  }, [copyShareLinkAndFinish]);

  const handleOverlayClose = useCallback(() => {
    dismissAndClose('backdrop');
  }, [dismissAndClose]);

  if (!isOpen) return null;

  return (
    <ResponsiveOverlay
      onClose={handleOverlayClose}
      presentation="sheet"
      panelClassName="share-modal-dialog"
      className="share-modal-overlay"
      aria-label={title}
    >
      <div className="share-modal-header">
          <Title name={title} as="h2" className="share-modal-title" />
          <button
            className="share-modal-close"
            onClick={() => dismissAndClose('close')}
            aria-label="Close share modal"
          >
            ✕
          </button>
        </div>

        <div className="share-modal-content gt-keyboard-scroll">
          {loading ? (
            <div style={{ padding: '24px', textAlign: 'center' }}>
              <p>Generating share link...</p>
            </div>
          ) : errorMessage ? (
            <div style={{ padding: '24px', textAlign: 'center', color: '#b42318' }}>
              <p>{errorMessage}</p>
            </div>
          ) : (
            <>
              {postPublishMessage ? (
                <div className="share-modal-celebration" aria-live="polite">
                  <div className="share-modal-celebration-icon-wrap">
                    <ShareButtonIcon size={28} />
                  </div>
                  <h3 className="share-modal-celebration-title">{postPublishMessage.title}</h3>
                  <p className="share-modal-celebration-subtitle">{postPublishMessage.subtitle}</p>
                </div>
              ) : null}
              <div className="share-modal-actions">
                <Button
                  variant="ghost"
                  onClick={handleCopyLink}
                  className="share-modal-action-button share-modal-copy-button"
                  fullWidth
                  disabled={!shareUrl}
                  startIcon={<ContentCopyIcon style={{ fontSize: 20 }} />}
                >
                  Copy Link
                </Button>
              </div>

              <div className="share-modal-url-section">
                <p className="share-modal-url-label">Link</p>
                {shareUrl ? (
                  <p 
                    className="share-modal-url-text" 
                    onClick={handleUrlClick}
                    title="Click to copy"
                    style={{ 
                      wordBreak: 'break-all',
                      overflowWrap: 'break-word',
                      cursor: 'pointer'
                    }}
                  >
                    {shareUrl}
                  </p>
                ) : (
                  <p className="share-modal-url-text" style={{ color: '#999' }}>
                    No link available
                  </p>
                )}
              </div>
            </>
          )}
        </div>
    </ResponsiveOverlay>
  );
};

export default ShareModal;
